import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../../utils/api";
import "./StudentDashboard.css";

function StudentDashboard({ user }) {
  const [documents, setDocuments] = useState([]);
  const [imageDocuments, setImageDocuments] = useState([]);
  const [audioDocuments, setAudioDocuments] = useState([]);
  const [subscriptionLabale, setSubscriptionLable] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState("");
  const [markingAttendance, setMarkingAttendance] = useState(false);
  const [activeTab, setActiveTab] = useState("documents");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response1 = apiService.getDocuments();
        const response2 = apiService.getImageDocuments();
        const response3 = apiService.getAudioDocuments();

        const [documentsResponse, imageDocumentsResponse , audioDocumentsResponse] = await Promise.all([response1, response2 , response3]);

        if (documentsResponse.data.success) {
          setDocuments(documentsResponse.data.documents);
          setImageDocuments(imageDocumentsResponse.data.documents);
          setAudioDocuments(audioDocumentsResponse.data.documents);
        } else {
          setError(documentsResponse.data.message || "Failed to fetch documents");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching documents");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchDashboard = async () => {
      try {
        const dashboardData = await apiService.getStudentDashboard();

        if (dashboardData?.data?.success) {
          setSubscriptionLable(dashboardData?.data?.data?.subscription);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching documents");
        console.error(err);
      }
    };

    fetchDocuments();
    fetchDashboard();
  }, [user]);

  const handleMarkAttendance = async () => {
    if (user && user.role === "student") {
      try {
        setMarkingAttendance(true);
        const response = await apiService.markAttendance();
        if (response.data.success) {
          setAttendanceMarked(true);
          setAttendanceMessage(response.data.message);
        }
      } catch (err) {
        console.error("Error marking attendance:", err);
        if (err.response && err.response.data) {
          setAttendanceMessage(err.response.data.message);
        } else {
          setAttendanceMessage("Failed to mark attendance. Please try again.");
        }
        setAttendanceMarked(false);
      } finally {
        setMarkingAttendance(false);
      }
    }
  };

  const handleViewDocument = (id) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user.role === "teacher") {
      navigate(`/documents/${id}`);
      return;
    }
    navigate(`/comparison-results/${id}`);
  };

  const handleViewImageDocument = (id) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user.role === "teacher") {
      navigate(`/comparison-image-results/${id}`);
      return;
    }
    navigate(`/comparison-image-results/${id}`);
  };

  const handleViewAudioDocument = (id) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user.role === "teacher") {
      navigate(`/comparison-audio-results/${id}`);
      return;
    }
    navigate(`/comparison-audio-results/${id}`);
  };

  const handleScanDocument = () => {
    navigate("/scan");
  };

  const handleWriteDocument = () => {
    navigate("/write");
  };

  const handleWriteImageDocument = () => {
    navigate("/select-image-document");
  };

  const handleWriteAudioDocument = () => {
    navigate("/select-audio-exercise-level");
  };

  const handleViewAttendance = () => {
    navigate("/attendance/history");
  };

  // Get current date and time
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Time greeting based on hour of day
  const hour = currentDate.getHours();
  let greeting;
  if (hour < 12) greeting = "Good Morning";
  else if (hour < 18) greeting = "Good Afternoon";
  else greeting = "Good Evening";

  // Calculate document stats
  const recentDocuments = documents.slice(0, 3);
  const totalDocuments = documents.length;
  const averageAccuracy = documents.length > 0 ? Math.round(documents.reduce((sum, doc) => sum + (doc.accuracy || 0), 0) / documents.length) : 0;

  return (
    <div className="student-dashboard">
      <div className="dashboard-hero">
        <div className="hero-content">
          <div className="greeting-section">
            <h1 className="greeting" style={{ color: "#fff" }}>
              {greeting}, {user.username}!
            </h1>
            <p className="date" style={{ color: "#fff" }}>
              {formattedDate}
            </p>
            {user && user.role === "student" && (
              <button onClick={handleMarkAttendance} className={`attendance-button ${markingAttendance ? "loading" : ""} ${attendanceMarked ? "marked" : ""}`} disabled={markingAttendance || attendanceMarked}>
                <span className="button-icon"></span>
                <span className="button-text">{markingAttendance ? "Marking..." : attendanceMarked ? "Attendance Marked" : "Mark Attendance"}</span>
              </button>
            )}

            <br />

            <div style={{ display: "flex" }}>
              <span className="attendance-button" style={{ cursor: "none" }}>
                Plan Expire:- {subscriptionLabale}
              </span>

              {subscriptionLabale === "No Plan" && (
                <button
                  className="attendance-button"
                  onClick={() => {
                    navigate("/subscription");
                  }}
                >
                  Enroll Now
                </button>
              )}
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat-card">
              <div className="stat-value">{totalDocuments}</div>
              <div className="stat-label">Total Documents</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{averageAccuracy}%</div>
              <div className="stat-label">Average Accuracy</div>
            </div>
          </div>
        </div>
      </div>

      {user && user.role === "student" && attendanceMessage && (
        <div className={`attendance-notification ${attendanceMarked ? "success" : "error"}`}>
          <span className="notification-icon"></span>
          {attendanceMessage}
        </div>
      )}

      <div className="dashboard-tabs">
        <button className={`tab-button ${activeTab === "documents" ? "active" : ""}`} onClick={() => setActiveTab("documents")}>
          <span className="tab-icon documents-icon"></span>
          Tests
        </button>
        <button className={`tab-button ${activeTab === "actions" ? "active" : ""}`} onClick={() => setActiveTab("actions")}>
          <span className="tab-icon actions-icon"></span>
          Quick Actions
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === "documents" && (
          <div className="documents-section">
            <div className="section-header">
              <h2>Your Tests</h2>
              {/* {documents.length > 0 && (
                <div className="view-options">
                  <button className="view-option active">
                    <span className="grid-icon"></span>
                  </button>
                  <button className="view-option">
                    <span className="list-icon"></span>
                  </button>
                </div>
              )} */}
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading your documents...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <span className="error-icon"></span>
                <p className="error-message">{error}</p>
                <button className="retry-button" onClick={() => window.location.reload()}>
                  Try Again
                </button>
              </div>
            ) : documents.length === 0 ? (
              <div className="empty-state">
                <div className="empty-illustration"></div>
                <h3>No documents yet</h3>
                <p>Start by writing your first document</p>
                <button onClick={handleWriteDocument} className="create-document-button">
                  Write Document
                </button>
              </div>
            ) : (
              <>
                <div className="student-documents-grid">
                  {documents.map((doc) => (
                    <div key={doc.id} className="document-card" onClick={() => handleViewDocument(doc.id)}>
                      <div className="document-card-header">
                        <div className={`document-type-badge ${doc.documentType === "written" ? "written" : "scanned"}`}>{doc.documentType === "written" ? "Written" : "Scanned"}</div>
                        <div className="document-menu">
                          <span className="menu-dots"></span>
                        </div>
                      </div>
                      <h3 className="document-title">{doc.title}</h3>
                      <div className="document-meta">
                        <div className="accuracy-meter">
                          <div className="accuracy-label">Accuracy</div>
                          <div className="accuracy-bar">
                            <div className="accuracy-fill" style={{ width: `${doc.accuracy || 0}%` }} data-level={doc.accuracy >= 80 ? "high" : doc.accuracy >= 50 ? "medium" : "low"}></div>
                          </div>
                          <div className="accuracy-value">{doc.accuracy || 0}%</div>
                        </div>
                        <div className="document-date">
                          <span className="date-icon"></span>
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="card-overlay">
                        <button className="view-button">View Result</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="section-header">
              <h2>Book Passage Tests</h2>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading your documents...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <span className="error-icon"></span>
                <p className="error-message">{error}</p>
                <button className="retry-button" onClick={() => window.location.reload()}>
                  Try Again
                </button>
              </div>
            ) : imageDocuments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-illustration"></div>
                <h3>No documents yet</h3>
                <p>Start by writing your first document</p>
                <button onClick={handleWriteImageDocument} className="create-document-button">
                  Write Document
                </button>
              </div>
            ) : (
              <>
                <div className="student-documents-grid">
                  {imageDocuments.map((doc) => (
                    <div key={doc.id} className="document-card" onClick={() => handleViewImageDocument(doc.id)}>
                      <div className="document-card-header">
                        <div className={`document-type-badge ${doc.documentType === "written" ? "written" : "scanned"}`}>{doc.documentType === "written" ? "Written" : "Scanned"}</div>
                        <div className="document-menu">
                          <span className="menu-dots"></span>
                        </div>
                      </div>
                      <h3 className="document-title">{doc.title}</h3>
                      <div className="document-meta">
                        <div className="accuracy-meter">
                          <div className="accuracy-label">Accuracy</div>
                          <div className="accuracy-bar">
                            <div className="accuracy-fill" style={{ width: `${doc.accuracy || 0}%` }} data-level={doc.accuracy >= 80 ? "high" : doc.accuracy >= 50 ? "medium" : "low"}></div>
                          </div>
                          <div className="accuracy-value">{doc.accuracy || 0}%</div>
                        </div>
                        <div className="document-date">
                          <span className="date-icon"></span>
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="card-overlay">
                        <button className="view-button">View Result</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="section-header">
              <h2>Audio Passage Tests</h2>
            </div>

            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading your documents...</p>
              </div>
            ) : error ? (
              <div className="error-container">
                <span className="error-icon"></span>
                <p className="error-message">{error}</p>
                <button className="retry-button" onClick={() => window.location.reload()}>
                  Try Again
                </button>
              </div>
            ) : audioDocuments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-illustration"></div>
                <h3>No documents yet</h3>
                <p>Start by writing your first document</p>
                <button onClick={handleWriteAudioDocument} className="create-document-button">
                  Write Document
                </button>
              </div>
            ) : (
              <>
                <div className="student-documents-grid">
                  {audioDocuments.map((doc) => (
                    <div key={doc.id} className="document-card" onClick={() => handleViewAudioDocument(doc.id)}>
                      <div className="document-card-header">
                        <div className={`document-type-badge ${doc.documentType === "written" ? "written" : "scanned"}`}>{doc.documentType === "written" ? "Written" : "Scanned"}</div>
                        <div className="document-menu">
                          <span className="menu-dots"></span>
                        </div>
                      </div>
                      <h3 className="document-title">{doc.title}</h3>
                      <div className="document-meta">
                        <div className="accuracy-meter">
                          <div className="accuracy-label">Accuracy</div>
                          <div className="accuracy-bar">
                            <div className="accuracy-fill" style={{ width: `${doc.accuracy || 0}%` }} data-level={doc.accuracy >= 80 ? "high" : doc.accuracy >= 50 ? "medium" : "low"}></div>
                          </div>
                          <div className="accuracy-value">{doc.accuracy || 0}%</div>
                        </div>
                        <div className="document-date">
                          <span className="date-icon"></span>
                          {new Date(doc.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="card-overlay">
                        <button className="view-button">View Result</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "actions" && (
          <div className="actions-section">
            <h2>Quick Actions</h2>
            <div className="actions-grid">
              {user.role === "student" && (
                <>
                  <div className="action-card" onClick={handleWriteDocument}>
                    <div className="action-icon write-icon"></div>
                    <h3>Give Test Document</h3>
                    <p>Create a new written document</p>
                  </div>

                  <div className="action-card" onClick={handleWriteImageDocument}>
                    <div className="action-icon write-icon"></div>
                    <h3>Give Passage Based Test</h3>
                    <p>“Book Passage Based Test”</p>
                  </div>

                  <div className="action-card" onClick={handleViewAttendance}>
                    <div className="action-icon attendance-icon"></div>
                    <h3>View Attendance</h3>
                    <p>Check your attendance history</p>
                  </div>
                  <div
                    className="action-card"
                    onClick={() => {
                      navigate("/profile");
                    }}
                  >
                    <div className="action-icon profile-icon"></div>
                    <h3>Edit Profile</h3>
                    <p>Update your personal information</p>
                  </div>
                  <div className="action-card" onClick={() => navigate("/subscription-details")}>
                    <div className="action-icon settings-icon"></div>
                    <h3>Course Subscription</h3>
                    <p>Manage your course subscription</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {documents.length > 0 && activeTab === "documents" && (
        <div className="recent-activity">
          <h2>Recent Activity</h2>
          <div className="activity-timeline">
            {recentDocuments.map((doc, index) => (
              <div key={doc.id} className="activity-item">
                <div className="activity-icon"></div>
                <div className="activity-content">
                  <p className="activity-text">
                    You {doc.documentType === "written" ? "wrote" : "scanned"} <strong>{doc.title}</strong>
                  </p>
                  <p className="activity-time">
                    {new Date(doc.createdAt).toLocaleDateString()} at {new Date(doc.createdAt).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentDashboard;
